import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Chip, TextField, MenuItem, Select,
  FormControl, InputLabel, InputAdornment, CircularProgress,
  IconButton, Tooltip, Badge, Paper, Divider, Stack, Grid,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Upload as UploadIcon,
  Phone as PhoneIcon,
  ViewKanban as KanbanIcon,
  List as ListIcon,
  ContactPhone as DialerIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Alarm as AlarmIcon,
  Badge as BadgeIcon,
  AccountBalance as MatchIcon,
} from '@mui/icons-material';
import { Badge as MuiBadge } from '@mui/material';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import salesAPI from '../../api/sales';
import fundedAPI from '../../api/funded';
import DialerView from './DialerView';
import PipelineView from './PipelineView';
import LeadsListView from './LeadsListView';
import LeadDetailDrawer from './LeadDetailDrawer';
import LeadFormDialog from './LeadFormDialog';
import CSVImportDialog from './CSVImportDialog';
import UserCardsView from './UserCardsView';

// ── Status config ────────────────────────────────────────────────────────────
export const STATUS_CONFIG = {
  new_lead:             { label: 'New Lead',            color: '#6366f1', bg: '#eef2ff' },
  contacted:            { label: 'Contacted',           color: '#0ea5e9', bg: '#e0f2fe' },
  qualified:            { label: 'Qualified',           color: '#f59e0b', bg: '#fffbeb' },
  application_sent:     { label: 'App Sent',            color: '#8b5cf6', bg: '#f5f3ff' },
  application_received: { label: 'App Received',        color: '#ec4899', bg: '#fdf2f8' },
  under_review:         { label: 'Under Review',        color: '#f97316', bg: '#fff7ed' },
  approved:             { label: 'Approved',            color: '#10b981', bg: '#ecfdf5' },
  funded:                 { label: 'Funded',              color: '#059669', bg: '#d1fae5' },
  declined_by_bank:       { label: 'Declined By Bank',    color: '#dc2626', bg: '#fef2f2' },
  client_declined_offer:  { label: 'Client Declined Offer', color: '#9333ea', bg: '#faf5ff' },
  not_interested:         { label: 'Not Interested',      color: '#6b7280', bg: '#f3f4f6' },
  do_not_call:            { label: 'Do Not Call',         color: '#ef4444', bg: '#fef2f2' },
  junk:                   { label: 'Junk',                color: '#78716c', bg: '#fafaf9' },
};

export const TIMEZONE_LABELS = {
  'America/New_York':   'Eastern (ET)',
  'America/Chicago':    'Central (CT)',
  'America/Denver':     'Mountain (MT)',
  'America/Phoenix':    'Mountain–AZ (no DST)',
  'America/Los_Angeles':'Pacific (PT)',
  'America/Anchorage':  'Alaska (AKT)',
  'Pacific/Honolulu':   'Hawaii (HT)',
  'America/Puerto_Rico':'Atlantic (AT)',
};

export const PRIORITY_COLORS = {
  low: '#94a3b8', medium: '#3b82f6', high: '#f59e0b', urgent: '#ef4444',
};

const VIEWS = [
  { id: 'cards',    label: 'Cards',    icon: <BadgeIcon /> },
  { id: 'dialer',   label: 'Dialer',   icon: <DialerIcon /> },
  { id: 'pipeline', label: 'Pipeline', icon: <KanbanIcon /> },
  { id: 'list',     label: 'List',     icon: <ListIcon /> },
];

// ─────────────────────────────────────────────────────────────────────────────
export default function SalesDashboard() {
  const navigate = useNavigate();
  const [view, setView] = useState('cards');
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [matchCount, setMatchCount] = useState(0);

  // Filters
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterTimezone, setFilterTimezone] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);

  // Dialogs
  const [selectedLead, setSelectedLead] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editLead, setEditLead] = useState(null);
  const [csvOpen, setCsvOpen] = useState(false);

  // Dialer card index
  const [dialerIndex, setDialerIndex] = useState(0);

  // ── Data fetch ─────────────────────────────────────────────────────────────
  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: view === 'pipeline' ? 200 : 100, // load more for pipeline
        sortBy,
        order: sortOrder,
      };
      if (search) params.search = search;
      if (filterStatus) params.status = filterStatus;
      if (filterTimezone) params.timezone = filterTimezone;
      if (filterPriority) params.priority = filterPriority;
      if (filterSource) params.source = filterSource;

      const res = await salesAPI.getLeads(params);
      setLeads(res.data.data);
      setTotal(res.data.total);
    } catch (err) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [page, search, filterStatus, filterTimezone, filterPriority, filterSource, sortBy, sortOrder, view]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await salesAPI.getStats();
      setStats(res.data.data);
    } catch {}
  }, []);

  const fetchMatchCount = useCallback(async () => {
    try {
      const res = await fundedAPI.getLeadMatches({ limit: 1 });
      setMatchCount(res.data.total || 0);
    } catch {}
  }, []);

  useEffect(() => { fetchLeads(); fetchStats(); fetchMatchCount(); }, [fetchLeads, fetchStats, fetchMatchCount]);

  // Reset dialer index when leads change
  useEffect(() => { setDialerIndex(0); }, [leads.length]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleLeadUpdated = (updatedLead) => {
    setLeads((prev) => prev.map((l) => l._id === updatedLead._id ? updatedLead : l));
    if (selectedLead?._id === updatedLead._id) setSelectedLead(updatedLead);
    fetchStats();
  };

  const handleLeadCreated = () => { fetchLeads(); fetchStats(); setFormOpen(false); setEditLead(null); };
  const handleCSVImported = () => { fetchLeads(); fetchStats(); setCsvOpen(false); };

  const handleOpenDetail = (lead) => { setSelectedLead(lead); setDrawerOpen(true); };
  const handleEditLead = (lead) => { setEditLead(lead); setFormOpen(true); };

  const handleLeadDeleted = (leadId) => {
    setLeads((prev) => prev.filter((l) => l._id !== leadId));
    if (selectedLead?._id === leadId) { setSelectedLead(null); setDrawerOpen(false); }
    fetchStats();
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      const res = await salesAPI.updateStatus(leadId, newStatus);
      handleLeadUpdated(res.data.data);
      toast.success(`Status updated to ${STATUS_CONFIG[newStatus]?.label}`);
    } catch { toast.error('Failed to update status'); }
  };

  // ── Stats cards ────────────────────────────────────────────────────────────
  const StatCard = ({ icon, value, label, color }) => (
    <Paper elevation={0} sx={{ p: 2, flex: 1, minWidth: 140, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Stack direction="row" alignItems="center" gap={1.5}>
        <Box sx={{ color, fontSize: 28 }}>{icon}</Box>
        <Box>
          <Typography variant="h5" fontWeight={700}>{value ?? '–'}</Typography>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
        </Box>
      </Stack>
    </Paper>
  );

  // ── Local time helper ──────────────────────────────────────────────────────
  const getLocalTimeForTz = (tz) => {
    try {
      return new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit', timeZone: tz, hour12: true }).format(new Date());
    } catch { return ''; }
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 1600, mx: 'auto' }}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" gap={2} mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={800} color="primary.main">
            📞 Sales CRM
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage leads, calls, emails, and your pipeline
          </Typography>
        </Box>
        <Stack direction="row" gap={1} flexWrap="wrap">
          <Tooltip title="Refresh">
            <IconButton onClick={() => { fetchLeads(); fetchStats(); }} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button startIcon={<UploadIcon />} variant="outlined" onClick={() => setCsvOpen(true)} size="small">
            Import CSV
          </Button>
          {matchCount > 0 && (
            <MuiBadge badgeContent={matchCount} color="warning" max={999}>
              <Button startIcon={<MatchIcon />} variant="outlined" color="warning" size="small" onClick={() => navigate('/sales/matches')}>
                Funded Matches
              </Button>
            </MuiBadge>
          )}
          <Button startIcon={<AddIcon />} variant="contained" onClick={() => { setEditLead(null); setFormOpen(true); }} size="small">
            New Lead
          </Button>
        </Stack>
      </Stack>

      {/* ── Stats ──────────────────────────────────────────────────────────── */}
      {stats && (
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} mb={3} flexWrap="wrap">
          <StatCard icon={<PeopleIcon />} value={stats.totalLeads} label="Total Leads" color="#6366f1" />
          <StatCard icon={<TrendingUpIcon />} value={stats.byStatus?.new_lead ?? 0} label="New This Batch" color="#0ea5e9" />
          <StatCard icon={<AlarmIcon />} value={stats.followUpsToday} label="Follow-ups Today" color="#f59e0b" />
          <StatCard icon={<MoneyIcon />} value={stats.fundedThisMonth} label="Funded This Month" color="#10b981" />
        </Stack>
      )}

      {/* ── Timezone clocks ─────────────────────────────────────────────────── */}
      <Stack direction="row" gap={1} mb={2} flexWrap="wrap">
        {Object.entries(TIMEZONE_LABELS).slice(0, 5).map(([tz, label]) => (
          <Chip
            key={tz}
            label={`${label.split(' ')[0]} ${getLocalTimeForTz(tz)}`}
            size="small"
            variant={filterTimezone === tz ? 'filled' : 'outlined'}
            color={filterTimezone === tz ? 'primary' : 'default'}
            onClick={() => setFilterTimezone(filterTimezone === tz ? '' : tz)}
            sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }}
          />
        ))}
      </Stack>

      {/* ── Filters ─────────────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ p: 1.5, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} alignItems="center" flexWrap="wrap">
          <TextField
            size="small"
            placeholder="Search business, name, email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ minWidth: 220 }}
          />

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select value={filterStatus} label="Status" onChange={(e) => setFilterStatus(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 155 }}>
            <InputLabel>Timezone</InputLabel>
            <Select value={filterTimezone} label="Timezone" onChange={(e) => setFilterTimezone(e.target.value)}>
              <MenuItem value="">All Zones</MenuItem>
              {Object.entries(TIMEZONE_LABELS).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Priority</InputLabel>
            <Select value={filterPriority} label="Priority" onChange={(e) => setFilterPriority(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {['urgent', 'high', 'medium', 'low'].map((p) => (
                <MenuItem key={p} value={p} sx={{ textTransform: 'capitalize' }}>{p}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 155 }}>
            <InputLabel>Source</InputLabel>
            <Select value={filterSource} label="Source" onChange={(e) => setFilterSource(e.target.value)}>
              <MenuItem value="">All Sources</MenuItem>
              <MenuItem value="website">Website</MenuItem>
              <MenuItem value="csv_import">CSV Import</MenuItem>
              <MenuItem value="manual">Manual</MenuItem>
              <MenuItem value="referral">Referral</MenuItem>
              <MenuItem value="cold_call">Cold Call</MenuItem>
              <MenuItem value="email_campaign">Email Campaign</MenuItem>
              <MenuItem value="application">Application</MenuItem>
              <MenuItem value="adobe sign historical">Adobe Sign Historical</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 135 }}>
            <InputLabel>Sort By</InputLabel>
            <Select value={sortBy} label="Sort By" onChange={(e) => setSortBy(e.target.value)}>
              <MenuItem value="createdAt">Date Added</MenuItem>
              <MenuItem value="timezone">Timezone</MenuItem>
              <MenuItem value="nextFollowUpDate">Follow-up Date</MenuItem>
              <MenuItem value="lastContactedAt">Last Contact</MenuItem>
              <MenuItem value="businessName">Business Name</MenuItem>
              <MenuItem value="status">Status</MenuItem>
            </Select>
          </FormControl>

          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            {total} lead{total !== 1 ? 's' : ''}
          </Typography>
        </Stack>
      </Paper>

      {/* ── View Switcher ────────────────────────────────────────────────────── */}
      <Stack direction="row" gap={1} mb={2}>
        {VIEWS.map((v) => (
          <Button
            key={v.id}
            size="small"
            startIcon={v.icon}
            variant={view === v.id ? 'contained' : 'outlined'}
            onClick={() => setView(v.id)}
          >
            {v.label}
          </Button>
        ))}
      </Stack>

      {/* ── Main View ───────────────────────────────────────────────────────── */}
      {loading && (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      )}

      {!loading && leads.length === 0 && (
        <>
          {view === 'cards' ? (
            <UserCardsView
              onOpenDetail={handleOpenDetail}
              onStatusChange={handleStatusChange}
              onLeadUpdated={handleLeadUpdated}
              onSyncComplete={() => { fetchLeads(); fetchStats(); }}
            />
          ) : (
            <Paper elevation={0} sx={{ textAlign: 'center', py: 8, border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
              <PhoneIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">No leads found. Import a CSV or add a lead manually.</Typography>
              <Stack direction="row" gap={1} justifyContent="center" mt={2}>
                <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => setCsvOpen(true)}>Import CSV</Button>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setFormOpen(true)}>Add Lead</Button>
              </Stack>
            </Paper>
          )}
        </>
      )}

      {!loading && leads.length > 0 && (
        <>
          {view === 'cards' && (
            <UserCardsView
              onOpenDetail={handleOpenDetail}
              onStatusChange={handleStatusChange}
              onLeadUpdated={handleLeadUpdated}
              onSyncComplete={() => { fetchLeads(); fetchStats(); }}
            />
          )}
          {view === 'dialer' && (
            <DialerView
              leads={leads}
              index={dialerIndex}
              onIndexChange={setDialerIndex}
              onLeadUpdated={handleLeadUpdated}
              onOpenDetail={handleOpenDetail}
              onStatusChange={handleStatusChange}
            />
          )}
          {view === 'pipeline' && (
            <PipelineView
              leads={leads}
              onLeadUpdated={handleLeadUpdated}
              onOpenDetail={handleOpenDetail}
              onStatusChange={handleStatusChange}
            />
          )}
          {view === 'list' && (
            <LeadsListView
              leads={leads}
              total={total}
              page={page}
              onPageChange={setPage}
              onOpenDetail={handleOpenDetail}
              onEditLead={handleEditLead}
              onStatusChange={handleStatusChange}
              onLeadUpdated={handleLeadUpdated}
            />
          )}
        </>
      )}

      {/* ── Lead Detail Drawer ───────────────────────────────────────────────── */}
      <LeadDetailDrawer
        open={drawerOpen}
        lead={selectedLead}
        onClose={() => setDrawerOpen(false)}
        onLeadUpdated={handleLeadUpdated}
        onLeadDeleted={handleLeadDeleted}
        onEditLead={handleEditLead}
        onStatusChange={handleStatusChange}
      />

      {/* ── Lead Form Dialog ─────────────────────────────────────────────────── */}
      <LeadFormDialog
        open={formOpen}
        lead={editLead}
        onClose={() => { setFormOpen(false); setEditLead(null); }}
        onSaved={handleLeadCreated}
      />

      {/* ── CSV Import Dialog ────────────────────────────────────────────────── */}
      <CSVImportDialog
        open={csvOpen}
        onClose={() => setCsvOpen(false)}
        onImported={handleCSVImported}
      />
    </Box>
  );
}
